// Taric Dazzle VFX — two phases:
//   1. WINDUP — pulsing hex previews show where the stun will land
//   2. BURST  — gem-shard explosion at each stunned enemy

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const WINDUP_MS = 600;
const BURST_MS  = 400;
const TOTAL     = WINDUP_MS + BURST_MS;

const TRAIL_COLOR = "#d4a820";
const GEM_COLOR   = "#44e8ff";
const SHARD_COUNT = 10;

const SHARD_ANGLES = Array.from({ length: SHARD_COUNT }, (_, i) =>
  (i / SHARD_COUNT) * Math.PI * 2,
);

function hexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 90);
    const x = cx + size * Math.cos(angle);
    const y = cy + size * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export class TaricDazzleVFX implements Animation {
  private elapsed = 0;

  constructor(
    private hexCenters: Point[],
    private hits: Point[],
    private hexSize: number,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const burstT = this.elapsed > WINDUP_MS
      ? (this.elapsed - WINDUP_MS) / BURST_MS
      : 0;

    ctx.save();

    // ── Phase 1: Hex preview highlights ──────────────────────────────────────
    if (burstT === 0) {
      const fadeIn    = Math.min(this.elapsed / 180, 1);
      const baseAlpha = fadeIn;
      const pulse     = 0.65 + Math.sin(this.elapsed * 0.012) * 0.35;

      for (const hc of this.hexCenters) {
        ctx.save();

        ctx.globalAlpha = baseAlpha * 0.18;
        ctx.fillStyle = GEM_COLOR;
        ctx.shadowBlur = 0;
        hexPath(ctx, hc.x, hc.y, this.hexSize * 0.88);
        ctx.fill();

        ctx.globalAlpha = baseAlpha * pulse * 0.9;
        ctx.strokeStyle = GEM_COLOR;
        ctx.lineWidth = 2.2;
        ctx.shadowColor = GEM_COLOR;
        ctx.shadowBlur = 14;
        hexPath(ctx, hc.x, hc.y, this.hexSize * 0.88);
        ctx.stroke();

        ctx.globalAlpha = baseAlpha * pulse * 0.55;
        ctx.strokeStyle = TRAIL_COLOR;
        ctx.lineWidth = 1.2;
        ctx.shadowColor = TRAIL_COLOR;
        ctx.shadowBlur = 8;
        hexPath(ctx, hc.x, hc.y, this.hexSize * 0.62);
        ctx.stroke();

        ctx.restore();
      }
    }

    // ── Phase 2: Burst at each stunned enemy ──────────────────────────────────
    if (burstT > 0 && this.hits.length > 0) {
      const fade = 1 - Easing.easeInQuad(burstT);

      for (const pt of this.hits) {
        ctx.save();
        ctx.translate(pt.x, pt.y);

        const ringR = 22 * Easing.easeOutCubic(burstT);

        ctx.globalAlpha = fade * 0.85;
        ctx.strokeStyle = TRAIL_COLOR;
        ctx.lineWidth = 3 * (1 - burstT) + 1;
        ctx.shadowColor = TRAIL_COLOR;
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.arc(0, 0, ringR, 0, Math.PI * 2);
        ctx.stroke();

        ctx.globalAlpha = fade * 0.65;
        ctx.strokeStyle = GEM_COLOR;
        ctx.lineWidth = 1.5;
        ctx.shadowColor = GEM_COLOR;
        ctx.shadowBlur = 14;
        ctx.beginPath();
        ctx.arc(0, 0, ringR * 0.52, 0, Math.PI * 2);
        ctx.stroke();

        const coreR = 10 * (1 - Easing.easeOutQuad(burstT));
        if (coreR > 0.5) {
          ctx.globalAlpha = fade * 0.9;
          ctx.fillStyle = "#ffffff";
          ctx.shadowColor = "#ffffff";
          ctx.shadowBlur = 16;
          ctx.beginPath();
          ctx.arc(0, 0, coreR, 0, Math.PI * 2);
          ctx.fill();
        }

        const shardDist = 28 * Easing.easeOutCubic(burstT);
        ctx.shadowBlur = 8;
        for (let i = 0; i < SHARD_COUNT; i++) {
          const angle = SHARD_ANGLES[i] + burstT * 0.5;
          const sz    = 3.5 * (1 - burstT * 0.8);
          if (sz <= 0) continue;
          ctx.globalAlpha = fade * 0.85;
          ctx.fillStyle   = i % 2 === 0 ? GEM_COLOR : TRAIL_COLOR;
          ctx.shadowColor = i % 2 === 0 ? GEM_COLOR : TRAIL_COLOR;
          ctx.save();
          ctx.translate(Math.cos(angle) * shardDist, Math.sin(angle) * shardDist);
          ctx.rotate(angle + Math.PI / 4);
          ctx.fillRect(-sz / 2, -sz / 2, sz, sz);
          ctx.restore();
        }

        ctx.restore();
      }
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
